import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LogIn, LogOut, LayoutDashboard, Shield, Heart, Menu, Truck, ClipboardList, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Toaster } from "@/components/ui/sonner";
import NotificationBell from "@/components/NotificationBell";

const ROLE_LABEL = { user: "Müşteri", business_owner: "Hizmet Sağlayıcı", admin: "Admin", driver: "Şoför" };

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/90 border-b border-border">
        <div className="mx-auto max-w-6xl px-4 h-14 flex items-center justify-between">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-2 font-black text-lg tracking-tight">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-[hsl(var(--brand-black))]">
              <Truck className="w-5 h-5 text-primary" />
            </span>
            <span className="font-extrabold">YOL <span className="text-primary bg-[hsl(var(--brand-black))] px-1.5 rounded">HATTI</span></span>
          </Link>
          <nav className="flex items-center gap-2">
            {user?.role === "business_owner" && (
              <Button variant="ghost" size="sm" onClick={() => navigate("/tasima-pazari")} data-testid="nav-market" className="hidden sm:inline-flex text-xs font-semibold">
                <Truck className="w-4 h-4 mr-1" /> Taşıma Pazarı
              </Button>
            )}
            {user?.role === "user" && (
              <>
                <Button variant="ghost" size="sm" onClick={() => navigate("/taleplerim")} data-testid="nav-taleplerim" className="hidden sm:inline-flex text-xs font-semibold">
                  <ClipboardList className="w-4 h-4 mr-1" /> Taleplerim
                </Button>
                <Button variant="ghost" size="sm" onClick={() => navigate("/tasima-yaptir")} data-testid="nav-new-req" className="hidden md:inline-flex text-xs font-semibold">
                  <Truck className="w-4 h-4 mr-1" /> Yeni Talep
                </Button>
              </>
            )}
            {user && <NotificationBell />}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button data-testid="user-menu-btn" variant="outline" size="sm" className="rounded-full">
                    <Menu className="w-4 h-4 mr-1" /> {user.name?.split(" ")[0] || ROLE_LABEL[user.role]}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  {user.role === "driver" && (
                    <DropdownMenuItem data-testid="menu-driver" onClick={() => navigate("/surucu")}>
                      <LayoutDashboard className="w-4 h-4 mr-2" /> Sürücü Ekranı
                    </DropdownMenuItem>
                  )}
                  {user.role !== "driver" && (
                    <DropdownMenuItem data-testid="menu-favorites" onClick={() => navigate("/favoriler")}>
                      <Heart className="w-4 h-4 mr-2" /> Favorilerim
                    </DropdownMenuItem>
                  )}
                  {user.role === "user" && (
                    <DropdownMenuItem data-testid="menu-profile" onClick={() => navigate("/profil")}>
                      <User className="w-4 h-4 mr-2" /> Profil / Bilgilerim
                    </DropdownMenuItem>
                  )}
                  {user.role === "user" && (
                    <DropdownMenuItem data-testid="menu-history" onClick={() => navigate("/yardim-taleplerim")}>
                      <LayoutDashboard className="w-4 h-4 mr-2" /> Yardım Taleplerim
                    </DropdownMenuItem>
                  )}
                  {user.role === "user" && (
                    <DropdownMenuItem data-testid="menu-transport-new" onClick={() => navigate("/tasima-yaptir")}>
                      <Truck className="w-4 h-4 mr-2" /> Yeni Taşıma Talebi
                    </DropdownMenuItem>
                  )}
                  {user.role === "user" && (
                    <DropdownMenuItem data-testid="menu-transport-mine" onClick={() => navigate("/taleplerim")}>
                      <ClipboardList className="w-4 h-4 mr-2" /> Taşıma Taleplerim
                    </DropdownMenuItem>
                  )}
                  {user.role === "business_owner" && (
                    <DropdownMenuItem data-testid="menu-market" onClick={() => navigate("/tasima-pazari")}>
                      <LayoutDashboard className="w-4 h-4 mr-2" /> Taşıma Pazarı
                    </DropdownMenuItem>
                  )}
                  {(user.role === "business_owner" || user.role === "admin") && (
                    <DropdownMenuItem data-testid="menu-owner" onClick={() => navigate("/panel")}>
                      <LayoutDashboard className="w-4 h-4 mr-2" /> Hizmet Sağlayıcı Paneli
                    </DropdownMenuItem>
                  )}
                  {user.role === "admin" && (
                    <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")}>
                      <Shield className="w-4 h-4 mr-2" /> Admin Paneli
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); navigate("/"); }}>
                    <LogOut className="w-4 h-4 mr-2" /> Çıkış Yap
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Button data-testid="login-btn" size="sm" variant="ghost" onClick={() => navigate("/giris")}>
                  <LogIn className="w-4 h-4 mr-1" /> Giriş
                </Button>
                <Button data-testid="register-btn" size="sm" onClick={() => navigate("/kayit")} className="rounded-full bg-primary text-[hsl(var(--brand-black))] hover:bg-primary/90 font-bold">
                  Üye Ol
                </Button>
              </>
            )}
          </nav>
        </div>
      </header>
      <main className="flex-1">{children}</main>
      <footer className="mt-16 bg-[hsl(var(--brand-black))] text-white">
        <div className="h-1 brand-stripe" />
        <div className="mx-auto max-w-6xl px-4 py-8 text-sm flex flex-col sm:flex-row gap-2 justify-between">
          <div>
            <p className="font-bold text-lg">YOL <span className="text-primary">HATTI</span></p>
            <p className="text-white/60 text-xs mt-1">Türkiye'nin Yol Yardım ve Taşıma Ağı — yolhatti.com</p>
          </div>
          <p className="text-xs text-white/60 self-end">© {new Date().getFullYear()} YOL HATTI</p>
        </div>
      </footer>
      <Toaster position="top-center" richColors />
    </div>
  );
}
